import AxiosClient from '../../utils/AxiosClient';
import { debounce } from 'lodash-es';
import { eventBus } from 'services/eventBus';
import {
  flagrNetworkNodeValueCache,
  hostListCache,
  networkNodesCache,
  networkNodeValueCache,
} from 'services/cache';
import { hostListFlag, HostListResponse } from './flagr';
import { defaultHostList } from './defaultHostList';

export interface Host {
  domain: string;
  delay?: number;
}

export const defaultLeyanbotHost = import.meta.env.VITE_LEYANBOT_HOST as string;
export const defaultFlagrHost = import.meta.env.VITE_FLAGR_ENDPOINT as string;

let leyanbotHost: string = networkNodeValueCache.get() ?? '';
let flagrHost: string = flagrNetworkNodeValueCache.get() ?? '';

export const getLeYanBotHost = () => {
  if (leyanbotHost) return leyanbotHost;
  leyanbotHost = defaultLeyanbotHost;
  networkNodeValueCache.set(leyanbotHost);
  return leyanbotHost;
};

export const getFlagrHost = () => {
  if (flagrHost) return flagrHost;
  flagrHost = defaultFlagrHost;
  flagrNetworkNodeValueCache.set(flagrHost);
  return flagrHost;
};

export const flagrClient = new AxiosClient({
  baseURL: `${getFlagrHost()}/flags/v1`,
});

export function patchClientsHost(allClients: AxiosClient[], host: string) {
  allClients.forEach((client) => {
    const config = client.getConfig();

    const { baseURL } = config;

    client.setConfig({
      ...config,
      baseURL: baseURL ? baseURL.replace(leyanbotHost, host) : baseURL,
    });
  });
  leyanbotHost = host;
  networkNodeValueCache.set(host);
  eventBus.emit('HOST_VALUE_CHANGE', host);
}

async function getHost(host: string) {
  const begin = performance.now();
  return fetch(`${host}/overseaim-store-home/health`).then(() => {
    const delay = performance.now() - begin;
    console.log('delay', host, delay);
    const cache = networkNodesCache.get<HostListResponse['leyanbot']>();
    if (cache) {
      cache.forEach((cur) => {
        if (cur.domain === host) {
          cur.delay = parseInt(`${delay}`, 10);
        }
      });
      networkNodesCache.set(cache);
      eventBus.emit('HOST_LIST_CHANGE', cache);
    }
    return host;
  });
}

export const refreshHostDelay = debounce(
  () => {
    const cache = hostListCache.get<HostListResponse>();

    if (cache) {
      Promise.all(
        cache.leyanbot.map(({ domain }) => {
          return getHost(domain);
        }),
      );
    }
  },
  3000,
  {
    leading: true,
    trailing: false,
  },
);

const COUNTER_MAX = 5;
const INTERVAL = 1000;
// 动态切换 host
export function switchHost(allClients: AxiosClient[]) {
  let timer: NodeJS.Timeout | null = null;
  let counter = 0;

  const reset = () => {
    counter = 0;
    if (timer) {
      clearTimeout(timer);
    }
  };

  const patch = debounce(
    () => {
      initHostV2(allClients);
    },
    15000,
    {
      leading: true,
      trailing: false,
    },
  );

  return () => {
    counter += 1;

    if (counter === COUNTER_MAX) {
      patch();
      reset();
    } else if (!timer) {
      timer = setTimeout(reset, INTERVAL);
    }
  };
}

const patchFlagrClientHost = (flagrClient: AxiosClient, host: string) => {
  const config = flagrClient.getConfig();

  const { baseURL } = config;

  flagrClient.setConfig({
    ...config,
    baseURL: baseURL ? baseURL.replace(flagrHost, host) : baseURL,
  });

  flagrHost = host;
  flagrNetworkNodeValueCache.set(host);
};

const patchClientsHostV2 = (
  cache: HostListResponse,
  allClients: AxiosClient[],
  flagrClient?: AxiosClient,
) => {
  Promise.any(cache.leyanbot.map(({ domain }) => getHost(domain)))
    .then((host) => {
      if (host !== leyanbotHost) {
        patchClientsHost(allClients, host);
      }
    })
    .catch(() => {});

  if (flagrClient) {
    Promise.any(cache.flagr.map(({ domain }) => getHost(domain)))
      .then((host) => {
        patchFlagrClientHost(flagrClient, host);
      })
      .catch(() => {});
  }
};

export const initHostV2 = async (allClients: AxiosClient[]) => {
  try {
    const response = await hostListFlag();
    let cache = hostListCache.get<HostListResponse>();
    if (response) {
      if (!cache || (cache && cache.version !== response.version)) {
        hostListCache.set(response);
        cache = response;
      }
    } else {
      hostListCache.set(defaultHostList);
      cache = defaultHostList;
    }
    networkNodesCache.set(cache.leyanbot);
    patchClientsHostV2(cache, allClients, flagrClient);
  } catch (err) {
    hostListCache.set(defaultHostList);
    networkNodesCache.set(defaultHostList.leyanbot);
    patchClientsHostV2(defaultHostList, allClients, flagrClient);
  }
};
